import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Sparkles, RefreshCcw } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { RehearsalResultsTable } from "@/components/domain/rehearsal-results-table"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type {
  TestingProps,
  PendingPromoteCard,
  RehearsalDetail,
  SuiteHealthSkill,
} from "@/types/agent_testing"

export default function TestingIndex({
  agents,
  selectedAgentId,
  pendingPromotes,
  latestRehearsal,
  agentSuiteHealth,
  testing_stream,
}: TestingProps) {
  const [busySkillIds, setBusySkillIds] = useState<Set<number>>(new Set())
  const [rerunningRehearsalId, setRerunningRehearsalId] = useState<number | null>(null)
  const [filter, setFilter] = useState({
    failureOnly: true,
    search: "",
  })

  useInertiaCable(testing_stream, {
    only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth"],
    debounce: 250,
    onRefresh: (payload) => {
      if (payload.model !== "TestRun" && payload.model !== "Rehearsal") return
      router.reload({ only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth"] })
      setBusySkillIds(new Set())
      if (payload.model === "Rehearsal") setRerunningRehearsalId(null)
    },
  })

  const handleAgentChange = (agentId: number) => {
    router.get("/testing", { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  async function csrfPost(path: string) {
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    return fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
    })
  }

  async function runRehearsal(skillId: number) {
    setBusySkillIds((prev) => new Set(prev).add(skillId))
    try {
      await csrfPost(`/skills/${skillId}/rehearsals`)
      router.reload({ only: ["pendingPromotes", "latestRehearsal"] })
    } catch {
      setBusySkillIds((prev) => {
        const next = new Set(prev); next.delete(skillId); return next
      })
    }
  }

  async function rerunFailedRehearsal(rehearsalId: number) {
    const skillId = latestRehearsal?.target_skill_id
    if (!skillId) return
    setRerunningRehearsalId(rehearsalId)
    try {
      const res = await csrfPost(`/skills/${skillId}/rehearsals/${rehearsalId}/rerun_failed`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        console.error("rerun_failed:", data.error ?? res.statusText)
        setRerunningRehearsalId(null)
        return
      }
      router.reload({ only: ["latestRehearsal", "pendingPromotes"] })
    } catch {
      setRerunningRehearsalId(null)
    }
  }

  async function promoteDraft(skillId: number) {
    setBusySkillIds((prev) => new Set(prev).add(skillId))
    try {
      await csrfPost(`/skills/${skillId}/promote`)
      router.reload({ only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth"] })
    } catch {
      setBusySkillIds((prev) => {
        const next = new Set(prev); next.delete(skillId); return next
      })
    }
  }

  if (agents.length === 0) {
    return (
      <AppShell>
        <PageHeader title="Agent Testing" subtitle="No agents yet — create one to start testing." />
        <Card className="p-6 text-sm text-app-muted">
          You don't have any agents in this company yet.
          <Link href="/agents" className="ml-2 text-brand-red hover:underline">Create an agent →</Link>
        </Card>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <PageHeader
        title="Agent Testing"
        subtitle="Pending draft promotions, the latest rehearsal, and the agent's overall suite health."
        actions={
          <select
            value={selectedAgentId ?? ""}
            onChange={(e) => handleAgentChange(parseInt(e.target.value, 10))}
            className="rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        }
      />

      <PendingPromotes
        cards={pendingPromotes}
        busySkillIds={busySkillIds}
        onRun={runRehearsal}
        onPromote={promoteDraft}
      />

      {latestRehearsal && (
        <LatestRehearsal
          rehearsal={latestRehearsal}
          filter={filter}
          onFilterChange={setFilter}
          onRerunFailed={rerunFailedRehearsal}
          isRerunning={rerunningRehearsalId === latestRehearsal.id}
          testStream={testing_stream}
        />
      )}

      <AgentSuiteHealth skills={agentSuiteHealth} />
    </AppShell>
  )
}

function PendingPromotes({ cards, busySkillIds, onRun, onPromote }:
  { cards: PendingPromoteCard[]
    busySkillIds: Set<number>
    onRun: (skillId: number) => void
    onPromote: (skillId: number) => void
  }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">Pending promote</h2>
      {cards.length === 0 ? (
        <Card className="p-6 text-sm text-app-muted">No drafts waiting to promote.</Card>
      ) : (
        <div className="space-y-3">
          {cards.map((c) => (
            <PendingPromoteRow
              key={c.skill_id}
              card={c}
              isBusy={busySkillIds.has(c.skill_id)}
              onRun={() => onRun(c.skill_id)}
              onPromote={() => onPromote(c.skill_id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function PendingPromoteRow({ card, isBusy, onRun, onPromote }:
  { card: PendingPromoteCard
    isBusy: boolean
    onRun: () => void
    onPromote: () => void
  }) {
  const r = card.rehearsal
  const isRunning = r?.state === "queued" || r?.state === "running"
  const rehearsalGreen = r?.state === "passed"
  const canRun = !isRunning && !isBusy
  const canPromote = card.per_skill_can_promote && rehearsalGreen
  const shouldShowRun = !r || r.state === "failed" || r.state === "errored"

  return (
    <Card className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link href={card.test_lab_path} className="font-medium text-app-text hover:underline">
            {card.skill_name}
          </Link>
          <code className="rounded bg-white/5 px-1 text-[11px] text-app-muted">{card.skill_key}</code>
          <Badge tone="red">v{card.draft_version_number} draft</Badge>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
          {card.per_skill_can_promote ? (
            <span className="inline-flex items-center gap-1 text-status-success">
              <CheckCircle2 size={12} /> per-skill checks pass
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-status-error">
              <XCircle size={12} /> {card.per_skill_blocker_count} blocker{card.per_skill_blocker_count === 1 ? "" : "s"}
            </span>
          )}
          <span className="text-app-muted">·</span>
          <RehearsalChip rehearsal={r} isStarting={isBusy && !r} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {shouldShowRun && (
          <Button size="sm" variant="ghost" onClick={onRun} disabled={!canRun}>
            <Sparkles size={14} className="mr-1" />
            {isBusy ? "Starting…" : r ? "Re-run rehearsal" : "Run rehearsal"}
          </Button>
        )}
        {r && (
          <a href="#rehearsal-results"
            className="rounded-control border border-border px-3 py-1.5 text-xs text-app-text transition hover:bg-white/5">
            View results
          </a>
        )}
        {canPromote && (
          <Button size="sm" variant="red" onClick={onPromote} disabled={isBusy}>
            Promote draft
          </Button>
        )}
      </div>
    </Card>
  )
}

function RehearsalChip({ rehearsal, isStarting }:
  { rehearsal: PendingPromoteCard["rehearsal"]; isStarting: boolean }) {
  if (isStarting) {
    return <span className="inline-flex items-center gap-1 text-app-muted"><Loader2 size={12} className="animate-spin" /> queueing</span>
  }
  if (!rehearsal) {
    return <span className="text-app-muted">no rehearsal yet</span>
  }
  if (rehearsal.state === "queued" || rehearsal.state === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-app-muted">
        <Loader2 size={12} className="animate-spin" /> rehearsal {rehearsal.state}
      </span>
    )
  }
  if (rehearsal.state === "passed") {
    return (
      <span className="inline-flex items-center gap-1 text-status-success">
        <CheckCircle2 size={12} /> rehearsal {rehearsal.passed_count}/{rehearsal.total} passed
      </span>
    )
  }
  if (rehearsal.state === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-status-error">
        <XCircle size={12} /> rehearsal {rehearsal.failed_count} failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-status-warning">
      <AlertTriangle size={12} /> rehearsal errored
    </span>
  )
}

function LatestRehearsal({ rehearsal, filter, onFilterChange, onRerunFailed, isRerunning, testStream }:
  { rehearsal: RehearsalDetail
    filter: { failureOnly: boolean; search: string }
    onFilterChange: (f: typeof filter) => void
    onRerunFailed: (rehearsalId: number) => void
    isRerunning: boolean
    testStream: string
  }) {
  return (
    <section id="rehearsal-results" className="space-y-3">
      <RehearsalTopStrip
        rehearsal={rehearsal}
        onRerunFailed={onRerunFailed}
        isRerunning={isRerunning}
      />

      <div className="flex flex-wrap items-center gap-2">
        <FilterToggle
          active={!filter.failureOnly}
          label="All"
          onClick={() => onFilterChange({ ...filter, failureOnly: false })}
        />
        <FilterToggle
          active={filter.failureOnly}
          label="Failures only"
          onClick={() => onFilterChange({ ...filter, failureOnly: true })}
        />
        <input
          type="text"
          value={filter.search}
          onChange={(e) => onFilterChange({ ...filter, search: e.target.value })}
          placeholder="Search scenarios…"
          className="ml-auto min-w-[180px] rounded-control border border-border bg-app-bg px-3 py-1.5 text-xs text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />
      </div>

      {/* onRerunFailed intentionally omitted — top strip owns it */}
      <RehearsalResultsTable rehearsal={rehearsal} filter={filter} testStream={testStream} />
    </section>
  )
}

// Top strip for the latest rehearsal: outcome chip + target/timestamp meta
// + (when terminal w/ failures) a single "Rerun failed" CTA. This is the
// sole rerun-failed surface — RehearsalResultsTable handles per-row reruns
// via the modal + per-row icon button.
function RehearsalTopStrip({
  rehearsal, onRerunFailed, isRerunning,
}: {
  rehearsal: RehearsalDetail
  onRerunFailed: (id: number) => void
  isRerunning: boolean
}) {
  const isTerminal =
    rehearsal.state === "passed" || rehearsal.state === "failed" || rehearsal.state === "errored"
  const tsLabel = rehearsal.finished_at
    ? `Finished ${fmtCompactRelative(rehearsal.finished_at)}`
    : `Started ${fmtCompactRelative(rehearsal.started_at)}`

  let chip: React.ReactNode
  if (rehearsal.state === "running" || rehearsal.state === "queued") {
    const finishedRuns = rehearsal.passed_count + rehearsal.failed_count + rehearsal.errored_count
    chip = (
      <span className="inline-flex items-center gap-1.5 rounded-control border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-xs text-sky-300">
        <Loader2 size={12} className="animate-spin" />
        Running: {finishedRuns}/{rehearsal.total} done
      </span>
    )
  } else if (rehearsal.failed_count > 0) {
    chip = (
      <span className="inline-flex items-center gap-1.5 rounded-control border border-status-error/40 bg-status-error/10 px-2.5 py-1 text-xs text-status-error">
        <XCircle size={12} />
        Failed: {rehearsal.failed_count} regression(s) of {rehearsal.total} runs
      </span>
    )
  } else if (rehearsal.errored_count > 0) {
    chip = (
      <span className="inline-flex items-center gap-1.5 rounded-control border border-status-warning/40 bg-status-warning/10 px-2.5 py-1 text-xs text-status-warning">
        <AlertTriangle size={12} />
        Errored: {rehearsal.errored_count} runs crashed
      </span>
    )
  } else {
    chip = (
      <span className="inline-flex items-center gap-1.5 rounded-control border border-status-success/40 bg-status-success/10 px-2.5 py-1 text-xs text-status-success">
        <CheckCircle2 size={12} />
        Passed cleanly: {rehearsal.total} runs
      </span>
    )
  }

  const failedTotal = rehearsal.failed_count + rehearsal.errored_count
  const showRerun =
    isTerminal && failedTotal > 0 && (rehearsal.state === "failed" || rehearsal.state === "errored")

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-app-panel px-4 py-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">
        Latest rehearsal
      </h2>
      {chip}
      <span className="text-[11px] text-app-muted">
        target: <code className="rounded bg-white/5 px-1">{rehearsal.target_skill_key}</code>{" "}
        v{rehearsal.target_skill_version_number} · {tsLabel}
      </span>
      {showRerun && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRerunFailed(rehearsal.id)}
          disabled={isRerunning}
          className="ml-auto"
        >
          <RefreshCcw size={14} className="mr-1" />
          {isRerunning ? "Rerunning…" : `Rerun failed (${failedTotal})`}
        </Button>
      )}
    </div>
  )
}

function FilterToggle({ active, label, onClick }:
  { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-control border px-2 py-1 text-xs transition",
        active
          ? "border-brand-red bg-brand-redSoft/30 text-app-text"
          : "border-border bg-app-bg text-app-muted hover:border-brand-red"
      )}
    >
      {label}
    </button>
  )
}

function AgentSuiteHealth({ skills }: { skills: SuiteHealthSkill[] }) {
  if (skills.length === 0) {
    return (
      <section id="suite-health" className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">Agent suite health</h2>
        <Card className="p-6 text-sm text-app-muted">No scenarios attached to this agent yet.</Card>
      </section>
    )
  }
  return (
    <section id="suite-health" className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">Agent suite health</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {skills.map((s) => (
          <SuiteHealthCard key={`${s.skill_key}-${s.skill_id ?? "_"}`} skill={s} />
        ))}
      </div>
    </section>
  )
}

function SuiteHealthCard({ skill }: { skill: SuiteHealthSkill }) {
  const allPassing = skill.passing_count === skill.total_count
  const [open, setOpen] = useState(false)
  const passRate = skill.total_count > 0 ? (skill.passing_count / skill.total_count) * 100 : 0

  // Latest run timestamp across this card's scenarios.
  const latestAt = skill.scenarios
    .map((sc) => sc.latest_run_at)
    .filter((t): t is string => !!t)
    .sort()
    .at(-1) ?? null

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-col gap-2 px-4 py-3 text-left transition hover:bg-white/5"
      >
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-app-text truncate">{skill.skill_name}</h4>
          <code className="rounded bg-white/5 px-1 text-[11px] text-app-muted">{skill.skill_key}</code>
        </div>
        <div className="h-1.5 rounded-full bg-app-panel2 overflow-hidden">
          <div
            className={cn(
              "h-full",
              allPassing ? "bg-status-success" : "bg-status-error",
            )}
            style={{ width: `${passRate}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] text-app-muted">
          <span className={cn(allPassing ? "text-status-success" : "text-status-error")}>
            {skill.passing_count}/{skill.total_count} passed
          </span>
          <span>{latestAt ? fmtCompactRelative(latestAt) : "—"}</span>
        </div>
      </button>

      {open && (
        <div className="divide-y divide-border border-t border-border">
          {skill.scenarios.map((sc) => {
            const href = sc.latest_run_id && skill.skill_id
              ? `/skills/${skill.skill_id}/test_runs/${sc.latest_run_id}`
              : skill.skill_id
                ? `/skills/${skill.skill_id}/test`
                : "#"
            return (
              <div
                key={sc.id}
                className="grid grid-cols-[24px_1fr_auto_auto] items-center gap-3 px-4 py-2.5 text-sm"
              >
                <SuiteStatusIcon status={sc.latest_status} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-app-text">{sc.name || sc.key}</span>
                    <ModeChip mode={sc.mode} />
                  </div>
                  <code className="text-[10px] text-app-muted">{sc.key}</code>
                </div>
                <span className={cn("text-[11px] uppercase tracking-wide", suiteStatusTone(sc.latest_status))}>
                  {sc.latest_status ?? "never run"}
                </span>
                {sc.latest_run_id ? (
                  <a href={href} className="text-[11px] text-brand-red hover:underline">View</a>
                ) : (
                  <span className="text-[11px] text-app-muted">—</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function SuiteStatusIcon({ status }: { status: string | null }) {
  if (status === "passed")  return <CheckCircle2 size={16} className="text-status-success" />
  if (status === "failed")  return <XCircle size={16} className="text-status-error" />
  if (status === "errored") return <AlertTriangle size={16} className="text-status-warning" />
  return <span className="inline-block h-2 w-2 rounded-full bg-app-muted/60" />
}

function suiteStatusTone(status: string | null): string {
  if (status === "passed")  return "text-status-success"
  if (status === "failed")  return "text-status-error"
  if (status === "errored") return "text-status-warning"
  return "text-app-muted"
}

function ModeChip({ mode }: { mode: "isolation" | "integration" }) {
  const isInt = mode === "integration"
  return (
    <span className={cn(
      "shrink-0 rounded-control border px-1.5 py-0.5 text-[9px] uppercase tracking-wide",
      isInt
        ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
        : "border-app-muted/30 bg-white/5 text-app-muted",
    )}>
      {isInt ? "int" : "iso"}
    </span>
  )
}
